/**
 * BreakGlassManagement - Admin page for reviewing and managing break glass events
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Loader2,
  Eye,
  Trash2,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import {
  breakGlassAPI,
  BreakGlassEvent,
  BreakGlassListParams,
  BreakGlassDeleteProgramDataRequest,
  BreakGlassDeleteProgramDataResponse,
} from '@/services/api/breakGlass';
import { useFormattedDate } from '../../hooks/useUserTimezone';

interface BreakGlassManagementProps {
  organizationId: number;
  organizationName: string;
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  approved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  flagged: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  violation: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const CATEGORY_LABELS: Record<string, string> = {
  medical_emergency: 'Medical Emergency',
  legal_requirement: 'Legal Requirement',
  safety_concern: 'Safety Concern',
};

export function BreakGlassManagement({ organizationId, organizationName }: BreakGlassManagementProps) {
  const { fmt } = useFormattedDate();
  const [events, setEvents] = useState<BreakGlassEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Delete Program Data state
  const [deleteMemberId, setDeleteMemberId] = useState<string>('');
  const [deleteProgramId, setDeleteProgramId] = useState<string>('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteResult, setDeleteResult] = useState<BreakGlassDeleteProgramDataResponse | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const pageSize = 15;

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: BreakGlassListParams = { page, page_size: pageSize };
      if (statusFilter) params.status = statusFilter;
      const result = await breakGlassAPI.listEvents(organizationId, params);
      setEvents(result.events);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load break glass events');
    } finally {
      setLoading(false);
    }
  }, [organizationId, statusFilter, page]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleReview = async (eventId: number, reviewStatus: string) => {
    setReviewLoading(true);
    setError(null);
    try {
      await breakGlassAPI.reviewEvent(organizationId, eventId, {
        review_status: reviewStatus,
        review_notes: reviewNotes,
      });
      setSuccess(`Event ${reviewStatus}`);
      setExpandedId(null);
      setReviewNotes('');
      fetchEvents();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to review event');
    } finally {
      setReviewLoading(false);
    }
  };

  const isDeleteFormValid =
    deleteMemberId !== '' &&
    deleteProgramId !== '' &&
    deleteReason.length >= 10 &&
    deleteConfirmed;

  const handleDeleteProgramData = async () => {
    if (!isDeleteFormValid) return;

    const confirmed = window.confirm(
      `WARNING: You are about to permanently delete all data for Member #${deleteMemberId} in Program #${deleteProgramId}. This action cannot be undone.\n\nAre you absolutely sure you want to proceed?`
    );
    if (!confirmed) return;

    setDeleteLoading(true);
    setDeleteError(null);
    setDeleteResult(null);
    try {
      const data: BreakGlassDeleteProgramDataRequest = {
        member_id: Number(deleteMemberId),
        program_id: Number(deleteProgramId),
        reason: deleteReason,
        confirm_deletion: true,
      };
      const result = await breakGlassAPI.deleteProgramData(organizationId, data);
      setDeleteResult(result);
      // Reset form on success
      setDeleteMemberId('');
      setDeleteProgramId('');
      setDeleteReason('');
      setDeleteConfirmed(false);
    } catch (err: any) {
      setDeleteError(err.response?.data?.detail || 'Failed to delete program data');
    } finally {
      setDeleteLoading(false);
    }
  };

  const pendingCount = events.filter((e) => e.review_status === 'pending').length;
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] text-ink-500">
          Emergency access events for {organizationName}
        </p>
        <button
          onClick={fetchEvents}
          disabled={loading}
          className="px-3 py-2 text-sm text-ink-500 hover:text-ink flex items-center gap-1.5"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
          <span className="text-sm text-green-700 dark:text-green-300">{success}</span>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">Status:</span>
              {['', 'pending', 'approved', 'flagged', 'violation'].map((s) => (
                <button
                  key={s}
                  onClick={() => { setStatusFilter(s); setPage(1); }}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                    statusFilter === s
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {s || 'All'}
                </button>
              ))}
            </div>
            {pendingCount > 0 && (
              <span className="ml-auto text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-2 py-1 rounded-full font-medium">
                {pendingCount} pending review
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Events Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500 mr-2" />
              <span className="text-gray-500">Loading events...</span>
            </div>
          ) : events.length === 0 ? (
            <div className="py-12 text-center">
              <Shield className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">No break glass events found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Initiator</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Active</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {events.map((event) => (
                    <React.Fragment key={event.id}>
                      <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {fmt.short(event.created_at)}{' '}
                          <span className="text-gray-400 text-xs">
                            {fmt.time(event.created_at)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {event.initiated_by_name || `User #${event.initiated_by}`}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {CATEGORY_LABELS[event.justification_category || ''] || event.justification_category}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[event.review_status] || 'bg-gray-100 text-gray-500'}`}>
                            {event.review_status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {event.is_active ? (
                            <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                              <Clock className="w-3 h-3" /> Active
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">Expired</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setExpandedId(expandedId === event.id ? null : event.id)}
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            {expandedId === event.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </button>
                        </td>
                      </tr>
                      {expandedId === event.id && (
                        <tr>
                          <td colSpan={6} className="px-4 py-4 bg-gray-50 dark:bg-gray-800/30">
                            <div className="space-y-3 max-w-3xl">
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400 text-xs">Reason</span>
                                  <p className="text-gray-700 dark:text-gray-300">{event.reason}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400 text-xs">Duration</span>
                                  <p className="text-gray-700 dark:text-gray-300">{event.access_duration_minutes} minutes</p>
                                </div>
                                {event.member_id && (
                                  <div>
                                    <span className="text-gray-500 dark:text-gray-400 text-xs">Member ID</span>
                                    <p className="text-gray-700 dark:text-gray-300">#{event.member_id}</p>
                                  </div>
                                )}
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400 text-xs">Expires</span>
                                  <p className="text-gray-700 dark:text-gray-300">{fmt.dateTime(event.expires_at)}</p>
                                </div>
                                {event.reviewed_by_name && (
                                  <div>
                                    <span className="text-gray-500 dark:text-gray-400 text-xs">Reviewed By</span>
                                    <p className="text-gray-700 dark:text-gray-300">{event.reviewed_by_name}</p>
                                  </div>
                                )}
                                {event.review_notes && (
                                  <div className="col-span-2">
                                    <span className="text-gray-500 dark:text-gray-400 text-xs">Review Notes</span>
                                    <p className="text-gray-700 dark:text-gray-300">{event.review_notes}</p>
                                  </div>
                                )}
                              </div>

                              {event.review_status === 'pending' && (
                                <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-2">
                                  <textarea
                                    value={reviewNotes}
                                    onChange={(e) => setReviewNotes(e.target.value)}
                                    placeholder="Review notes (optional)..."
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm resize-none"
                                    disabled={reviewLoading}
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleReview(event.id, 'approved')}
                                      disabled={reviewLoading}
                                      className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                                    >
                                      <CheckCircle className="w-3.5 h-3.5" /> Approve
                                    </button>
                                    <button
                                      onClick={() => handleReview(event.id, 'flagged')}
                                      disabled={reviewLoading}
                                      className="px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-100 dark:bg-orange-900/20 rounded-lg hover:bg-orange-200 disabled:opacity-50 flex items-center gap-1"
                                    >
                                      <AlertTriangle className="w-3.5 h-3.5" /> Flag
                                    </button>
                                    <button
                                      onClick={() => handleReview(event.id, 'violation')}
                                      disabled={reviewLoading}
                                      className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 dark:bg-red-900/20 rounded-lg hover:bg-red-200 disabled:opacity-50 flex items-center gap-1"
                                    >
                                      <XCircle className="w-3.5 h-3.5" /> Violation
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-700">
              <span className="text-xs text-gray-500">{total} events total</span>
              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-2.5 py-1 text-xs rounded ${
                      page === p
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Program Data Section */}
      <Card className="border-red-200 dark:border-red-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertTriangle className="w-5 h-5" />
            <Trash2 className="w-5 h-5" />
            Break Glass: Delete Program Data
          </CardTitle>
          <CardDescription className="text-red-600/80 dark:text-red-400/80">
            Permanently deletes all member data associated with a specific program. This action is
            irreversible and will be logged as a break glass event for audit purposes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Delete Error Banner */}
          {deleteError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700 dark:text-red-300">{deleteError}</span>
              <button
                onClick={() => setDeleteError(null)}
                className="ml-auto text-red-400 hover:text-red-600 text-xs"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Delete Success Banner */}
          {deleteResult && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span className="text-sm font-medium text-green-700 dark:text-green-300">
                  {deleteResult.message}
                </span>
                <button
                  onClick={() => setDeleteResult(null)}
                  className="ml-auto text-green-400 hover:text-green-600 text-xs"
                >
                  Dismiss
                </button>
              </div>
              <div className="text-sm text-green-700 dark:text-green-300 space-y-1 pl-6">
                <p>Break Glass Event ID: <span className="font-mono">#{deleteResult.break_glass_event_id}</span></p>
                <p>Member ID: <span className="font-mono">#{deleteResult.member_id}</span></p>
                <p>Program ID: <span className="font-mono">#{deleteResult.program_id}</span></p>
                {Object.keys(deleteResult.deleted_records).length > 0 && (
                  <div>
                    <p className="font-medium mt-1">Deleted Records:</p>
                    <ul className="list-disc list-inside ml-2">
                      {Object.entries(deleteResult.deleted_records).map(([table, count]) => (
                        <li key={table}>
                          {table}: <span className="font-mono">{count}</span> record{count !== 1 ? 's' : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Member ID
              </label>
              <input
                type="number"
                value={deleteMemberId}
                onChange={(e) => setDeleteMemberId(e.target.value)}
                placeholder="Enter member ID"
                min={1}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                disabled={deleteLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Program ID
              </label>
              <input
                type="number"
                value={deleteProgramId}
                onChange={(e) => setDeleteProgramId(e.target.value)}
                placeholder="Enter program ID"
                min={1}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                disabled={deleteLoading}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reason for Deletion
              {deleteReason.length > 0 && deleteReason.length < 10 && (
                <span className="text-red-500 text-xs ml-2">
                  (minimum 10 characters - {10 - deleteReason.length} more needed)
                </span>
              )}
            </label>
            <textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Provide a detailed reason for this data deletion (minimum 10 characters)..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              disabled={deleteLoading}
            />
          </div>

          {/* Confirmation Checkbox */}
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={deleteConfirmed}
              onChange={(e) => setDeleteConfirmed(e.target.checked)}
              className="mt-0.5 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              disabled={deleteLoading}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
              I confirm this is an irreversible data deletion
            </span>
          </label>

          {/* Delete Button */}
          <div className="pt-2">
            <button
              onClick={handleDeleteProgramData}
              disabled={!isDeleteFormValid || deleteLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {deleteLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Delete Program Data
                </>
              )}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
